import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services/reportService';
import { Report, ReportStatus, REPORT_CATEGORY_INFO } from '../../types/reporting';
import { LoadingSpinner } from '../../components/ui';

export function ModerationDashboard() {
  const { user, isAdmin: userIsAdmin } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<ReportStatus | 'all'>('pending');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!userIsAdmin) return;
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, userIsAdmin]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const status = selectedStatus === 'all' ? undefined : selectedStatus;
      const { reports: fetchedReports } = await reportService.getReportsQueue(status);
      setReports(fetchedReports);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (reportId: string, newStatus: ReportStatus) => {
    if (!user) return;
    
    try {
      setActionLoading(true);
      await reportService.updateReportStatus(reportId, newStatus, user.uid);
      
      // Update local state
      setReports(prev => prev.map(report => 
        report.id === reportId 
          ? { ...report, status: newStatus, assignedTo: user.uid }
          : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => prev ? { ...prev, status: newStatus, assignedTo: user.uid } : null);
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Failed to update report status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNotes = async (reportId: string, notes: string) => {
    if (!user || !notes.trim()) return;
    
    try {
      setActionLoading(true);
      await reportService.addModeratorNotes(reportId, notes.trim(), user.uid);
      
      // Update local state
      setReports(prev => prev.map(report => 
        report.id === reportId 
          ? { ...report, moderatorNotes: notes.trim(), assignedTo: user.uid }
          : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => prev ? { ...prev, moderatorNotes: notes.trim(), assignedTo: user.uid } : null);
      }
    } catch (error) {
      console.error('Error adding notes:', error);
      alert('Failed to add notes');
    } finally {
      setActionLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-700 bg-red-100';
      case 'high': return 'text-orange-700 bg-orange-100';
      case 'medium': return 'text-yellow-700 bg-yellow-100';
      default: return 'text-green-700 bg-green-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-blue-700 bg-blue-100';
      case 'under_review': return 'text-yellow-700 bg-yellow-100';
      case 'resolved': return 'text-green-700 bg-green-100';
      case 'dismissed': return 'text-gray-700 bg-gray-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  if (!userIsAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the moderation dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Moderation Dashboard</h1>
          <p className="text-gray-600">Review and manage community reports</p>
        </div>

        <div className="flex gap-8">
          {/* Reports List */}
          <div className="flex-1">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as ReportStatus | 'all')}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Reports</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                
                <button
                  onClick={loadReports}
                  className="ml-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Reports List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : reports.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-600">No reports found for the selected filters.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer transition-colors ${
                      selectedReport?.id === report.id ? 'ring-2 ring-primary-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedReport(report)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(report.priority)}`}>
                            {report.priority.toUpperCase()}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                            {report.status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">
                            {report.type === 'event' ? '📅' : '👤'} {report.type}
                          </span>
                        </div>
                        
                        <h3 className="font-medium text-gray-900 mb-1">
                          {REPORT_CATEGORY_INFO[report.category].title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          Target: <span className="font-medium">{report.targetTitle}</span>
                        </p>
                        
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {report.reason}
                        </p>
                      </div>
                      
                      <div className="text-right text-xs text-gray-500">
                        {report.createdAt.toDate().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Report Details Panel */}
          {selectedReport && (
            <div className="w-96">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Report Details</h2>
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Basic Info */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Category</label>
                    <p className="text-sm text-gray-900">
                      {REPORT_CATEGORY_INFO[selectedReport.category].title}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Target</label>
                    <p className="text-sm text-gray-900">
                      {selectedReport.targetTitle} ({selectedReport.type})
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Reason</label>
                    <p className="text-sm text-gray-900">{selectedReport.reason}</p>
                  </div>

                  {selectedReport.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Description</label>
                      <p className="text-sm text-gray-900">{selectedReport.description}</p>
                    </div>
                  )}

                  {selectedReport.evidence && selectedReport.evidence.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Evidence</label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {selectedReport.evidence.map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt={`Evidence ${index + 1}`}
                            className="w-full h-20 object-cover rounded-md border"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Moderator Notes */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Moderator Notes</label>
                    <textarea
                      placeholder="Add your notes..."
                      defaultValue={selectedReport.moderatorNotes || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (selectedReport.moderatorNotes || '')) {
                          handleAddNotes(selectedReport.id, e.target.value);
                        }
                      }}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={3}
                    />
                  </div>

                  {/* Actions */}
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Actions</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleStatusUpdate(selectedReport.id, 'under_review')}
                        disabled={actionLoading || selectedReport.status === 'under_review'}
                        className="px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 text-sm"
                      >
                        Review
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedReport.id, 'resolved')}
                        disabled={actionLoading || selectedReport.status === 'resolved'}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                      >
                        Resolve
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedReport.id, 'dismissed')}
                        disabled={actionLoading || selectedReport.status === 'dismissed'}
                        className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 text-sm"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedReport.id, 'pending')}
                        disabled={actionLoading || selectedReport.status === 'pending'}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                      >
                        Reopen
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="border-t pt-4 text-xs text-gray-500">
                    <p>Created: {selectedReport.createdAt.toDate().toLocaleString()}</p>
                    <p>Updated: {selectedReport.updatedAt.toDate().toLocaleString()}</p>
                    {selectedReport.assignedTo && (
                      <p>Assigned to: {selectedReport.assignedTo}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}